import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Shield, AlertCircle } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { PersonalAnswers, OrganizationAnswers, calculatePersonalScore, calculateOrganizationScore, AssessmentResult } from '../utils/riskScoring';
// Personal Assessment Questions (6 steps)
const PERSONAL_QUESTIONS = [{
  step: 1,
  title: 'Identity & Account Security',
  questions: [{
    id: 'passwordManager',
    question: 'Do you use a password manager?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 2
    }, {
      label: 'No',
      value: 'no',
      points: 0
    }]
  }, {
    id: 'mfaEmail',
    question: 'Is multi-factor authentication (MFA or passkeys) enabled on your primary email?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 2
    }, {
      label: 'No',
      value: 'no',
      points: 0
    }]
  }]
}, {
  step: 2,
  title: 'Password & Access Hygiene',
  questions: [{
    id: 'passwordReuse',
    question: 'Do you reuse passwords across multiple accounts?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 0
    }, {
      label: 'Sometimes',
      value: 'sometimes',
      points: 1
    }, {
      label: 'No',
      value: 'no',
      points: 2
    }]
  }, {
    id: 'recoveryOptions',
    question: 'Have you reviewed account recovery options (backup email, phone, recovery codes)?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 2
    }, {
      label: 'Not sure',
      value: 'not-sure',
      points: 1
    }, {
      label: 'No',
      value: 'no',
      points: 0
    }]
  }]
}, {
  step: 3,
  title: 'Device Security',
  questions: [{
    id: 'deviceEncryption',
    question: 'Are your main devices (phone/laptop) encrypted?',
    options: [{
      label: 'Yes, all devices',
      value: 'all',
      points: 2
    }, {
      label: 'Some devices',
      value: 'some',
      points: 1
    }, {
      label: 'No / Not sure',
      value: 'no',
      points: 0
    }]
  }, {
    id: 'autoUpdates',
    question: 'Do you regularly update your operating system and apps?',
    options: [{
      label: 'Automatic updates',
      value: 'automatic',
      points: 2
    }, {
      label: 'Manual / irregular',
      value: 'manual',
      points: 1
    }, {
      label: 'Rarely',
      value: 'rarely',
      points: 0
    }]
  }]
}, {
  step: 4,
  title: 'Data Backup & Recovery',
  questions: [{
    id: 'backups',
    question: 'Do you have backups of important data?',
    options: [{
      label: 'Cloud + offline',
      value: 'cloud-offline',
      points: 2
    }, {
      label: 'Cloud only',
      value: 'cloud-only',
      points: 1
    }, {
      label: 'No backups',
      value: 'no',
      points: 0
    }]
  }]
}, {
  step: 5,
  title: 'Digital Habits',
  questions: [{
    id: 'verifyLinks',
    question: 'Do you verify links and attachments before opening them?',
    options: [{
      label: 'Always',
      value: 'always',
      points: 2
    }, {
      label: 'Sometimes',
      value: 'sometimes',
      points: 1
    }, {
      label: 'Rarely',
      value: 'rarely',
      points: 0
    }]
  }, {
    id: 'breachCheck',
    question: 'Have you ever checked whether your email appears in a data breach?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 2
    }, {
      label: 'No',
      value: 'no',
      points: 0
    }]
  }]
}, {
  step: 6,
  title: 'Incident Awareness',
  questions: [{
    id: 'incidentAwareness',
    question: 'If one of your accounts is compromised, do you know what to do first?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 2
    }, {
      label: 'Not confident',
      value: 'not-confident',
      points: 1
    }, {
      label: 'No idea',
      value: 'no',
      points: 0
    }]
  }]
}];
// Organization Assessment Questions (6 steps)
const ORGANIZATION_QUESTIONS = [{
  step: 1,
  title: 'Governance & Policy',
  questions: [{
    id: 'securityPolicy',
    question: 'Does your organization have a documented cybersecurity or information security policy?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 2
    }, {
      label: 'Draft only',
      value: 'draft',
      points: 1
    }, {
      label: 'No',
      value: 'no',
      points: 0
    }]
  }]
}, {
  step: 2,
  title: 'Identity & Access Management',
  questions: [{
    id: 'mfaEnforced',
    question: 'Is MFA enforced for critical accounts (email, admin, cloud)?',
    options: [{
      label: 'Mandatory',
      value: 'mandatory',
      points: 2
    }, {
      label: 'Partial',
      value: 'partial',
      points: 1
    }, {
      label: 'No',
      value: 'no',
      points: 0
    }]
  }, {
    id: 'accessReview',
    question: 'Are access rights reviewed when staff join, change roles, or leave?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 2
    }, {
      label: 'Informal',
      value: 'informal',
      points: 1
    }, {
      label: 'No',
      value: 'no',
      points: 0
    }]
  }]
}, {
  step: 3,
  title: 'Device & Endpoint Security',
  questions: [{
    id: 'deviceManagement',
    question: 'Are company devices centrally managed and updated?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 2
    }, {
      label: 'Partially',
      value: 'partially',
      points: 1
    }, {
      label: 'No',
      value: 'no',
      points: 0
    }]
  }]
}, {
  step: 4,
  title: 'Data Protection & Backup',
  questions: [{
    id: 'dataClassification',
    question: 'Is sensitive data classified and protected?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 2
    }, {
      label: 'Partially',
      value: 'partially',
      points: 1
    }, {
      label: 'No',
      value: 'no',
      points: 0
    }]
  }, {
    id: 'backupTested',
    question: 'Are backups performed and tested for restoration?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 2
    }, {
      label: 'Backup only',
      value: 'backup-only',
      points: 1
    }, {
      label: 'No',
      value: 'no',
      points: 0
    }]
  }]
}, {
  step: 5,
  title: 'Monitoring & Detection',
  questions: [{
    id: 'securityMonitoring',
    question: 'Are security logs or events monitored?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 2
    }, {
      label: 'Limited',
      value: 'limited',
      points: 1
    }, {
      label: 'No',
      value: 'no',
      points: 0
    }]
  }]
}, {
  step: 6,
  title: 'Incident Readiness',
  questions: [{
    id: 'incidentPlan',
    question: 'Does your organization have an incident response plan?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 2
    }, {
      label: 'Informal',
      value: 'informal',
      points: 1
    }, {
      label: 'No',
      value: 'no',
      points: 0
    }]
  }, {
    id: 'securityTraining',
    question: 'Have employees received basic cybersecurity awareness training?',
    options: [{
      label: 'Yes',
      value: 'yes',
      points: 2
    }, {
      label: 'Occasionally',
      value: 'occasionally',
      points: 1
    }, {
      label: 'No',
      value: 'no',
      points: 0
    }]
  }]
}];
export function QuizWizard() {
  const [searchParams] = useSearchParams();
  const assessmentType = (searchParams.get('type') || 'personal') as 'personal' | 'organization';
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Partial<PersonalAnswers | OrganizationAnswers>>({});
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const questions = assessmentType === 'personal' ? PERSONAL_QUESTIONS : ORGANIZATION_QUESTIONS;
  const totalSteps = questions.length;
  const currentStepData = questions[currentStep];
  const handleAnswer = (questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };
  const canProceed = () => {
    return currentStepData.questions.every(q => answers[q.id as keyof typeof answers]);
  };
  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Calculate results
      if (assessmentType === 'personal') {
        const result = calculatePersonalScore(answers as PersonalAnswers);
        setResult(result);
      } else {
        const result = calculateOrganizationScore(answers as OrganizationAnswers);
        setResult(result);
      }
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  // Results Page
  if (result) {
    return <div className="max-w-4xl mx-auto space-y-8 animate-fade-in-up" data-id="element-495">
        {/* Header */}
        <Card glass padding="lg" className="text-center" data-id="element-496">
          <Badge variant="primary" size="lg" className="mb-4" data-id="element-497">
            Assessment Complete
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-2" data-id="element-498">
            {assessmentType === 'personal' ? 'Your Personal Cybersecurity Risk Level' : 'Organizational Cybersecurity Risk Overview'}
          </h2>
        </Card>

        {/* Score Display */}
        <Card glass padding="lg" className="text-center border-2 border-cyan-500/20" data-id="element-499">
          <div className="mb-6" data-id="element-500">
            <div className={`text-7xl md:text-8xl font-bold ${result.color} mb-2`} data-id="element-501">
              {result.score}
            </div>
            <div className="text-2xl text-slate-400 mb-4" data-id="element-502">out of 100</div>
            <Badge variant={result.level === 'Strong' ? 'success' : result.level === 'Good' ? 'primary' : result.level === 'Medium Risk' ? 'warning' : 'danger'} size="lg" data-id="element-503">
              {result.level}
            </Badge>
          </div>
          <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto" data-id="element-504">
            {result.summary}
          </p>
        </Card>

        {/* Top Priorities */}
        <Card glass padding="lg" data-id="element-505">
          <div className="flex items-center mb-6" data-id="element-506">
            <AlertCircle className="h-6 w-6 text-amber-400 mr-3" data-id="element-507" />
            <h3 className="text-2xl font-bold text-slate-100" data-id="element-508">
              Top {result.priorities.length} Priority Actions
            </h3>
          </div>
          <div className="space-y-4" data-id="element-509">
            {result.priorities.map((priority, index) => <div key={index} className="flex items-start p-4 rounded-lg bg-slate-900/50 border border-slate-800" data-id="element-510">
                <div className="h-8 w-8 rounded-full bg-cyan-500/10 flex items-center justify-center text-cyan-400 font-bold mr-4 flex-shrink-0" data-id="element-511">
                  {index + 1}
                </div>
                <p className="text-slate-300 pt-1" data-id="element-512">{priority}</p>
              </div>)}
          </div>
        </Card>

        {/* Roadmap (Organization only) */}
        {assessmentType === 'organization' && result.roadmap && <Card glass padding="lg" data-id="element-513">
            <h3 className="text-2xl font-bold text-slate-100 mb-6 flex items-center" data-id="element-514">
              <Shield className="h-6 w-6 text-cyan-400 mr-3" data-id="element-515" />
              30 / 60 / 90-Day Roadmap
            </h3>
            <div className="grid md:grid-cols-3 gap-6" data-id="element-516">
              <RoadmapColumn title="30 Days" items={result.roadmap.day30} color="text-cyan-400" data-id="element-517" />
              <RoadmapColumn title="60 Days" items={result.roadmap.day60} color="text-emerald-400" data-id="element-518" />
              <RoadmapColumn title="90 Days" items={result.roadmap.day90} color="text-amber-400" data-id="element-519" />
            </div>
          </Card>}

        {/* Next Steps */}
        <Card glass padding="lg" className="bg-gradient-to-br from-cyan-500/5 to-emerald-500/5 border-cyan-500/20" data-id="element-520">
          <h3 className="text-xl font-bold text-slate-100 mb-4" data-id="element-521">Next Steps</h3>
          <div className="space-y-3" data-id="element-522">
            <Link to={assessmentType === 'personal' ? '/personal' : '/organization'} data-id="element-523">
              <Button variant="primary" size="lg" fullWidth className="justify-between" data-id="element-524">
                <span data-id="element-525">
                  View{' '}
                  {assessmentType === 'personal' ? 'Personal' : 'Organization'}{' '}
                  Security Protocol
                </span>
                <ArrowRight className="h-5 w-5" data-id="element-526" />
              </Button>
            </Link>
            {assessmentType === 'organization' && <Link to="/compliance/global" data-id="element-527">
                <Button variant="outline" size="lg" fullWidth className="justify-between" data-id="element-528">
                  <span data-id="element-529">View Compliance Mapping</span>
                  <ArrowRight className="h-5 w-5" data-id="element-530" />
                </Button>
              </Link>}
          </div>
        </Card>
      </div>;
  }
  // Assessment Questions
  return <div className="max-w-3xl mx-auto" data-id="element-531">
      <Card glass padding="lg" data-id="element-532">
        {/* Progress Bar */}
        <div className="mb-8" data-id="element-533">
          <div className="flex justify-between text-sm text-slate-400 mb-3" data-id="element-534">
            <span className="font-mono" data-id="element-535">
              Step {currentStep + 1} of {totalSteps}
            </span>
            <span className="font-mono" data-id="element-536">
              {Math.round((currentStep + 1) / totalSteps * 100)}%
            </span>
          </div>
          <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden" data-id="element-537">
            <div className="bg-gradient-to-r from-cyan-500 to-emerald-500 h-full transition-all duration-500 ease-out" style={{
            width: `${(currentStep + 1) / totalSteps * 100}%`
          }} data-id="element-538" />
          </div>
        </div>

        {/* Step Title */}
        <div className="mb-8" data-id="element-539">
          <Badge variant="primary" size="md" className="mb-4" data-id="element-540">
            {currentStepData.title}
          </Badge>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-100" data-id="element-541">
            {assessmentType === 'personal' ? 'Personal' : 'Organization'}{' '}
            Security Assessment
          </h2>
        </div>

        {/* Questions */}
        <div className="space-y-8" data-id="element-542">
          {currentStepData.questions.map((q, qIndex) => <div key={q.id} data-id="element-543">
              <h3 className="text-lg font-semibold text-slate-200 mb-4" data-id="element-544">
                {currentStepData.questions.length > 1 && `${qIndex + 1}. `}
                {q.question}
              </h3>
              <div className="space-y-3" data-id="element-545">
                {q.options.map(option => {
              const isSelected = answers[q.id as keyof typeof answers] === option.value;
              return <button key={option.value} onClick={() => handleAnswer(q.id, option.value)} className={`w-full text-left p-4 rounded-lg border-2 transition-all ${isSelected ? 'bg-cyan-500/10 border-cyan-500 text-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.3)]' : 'bg-slate-900/50 border-slate-700 text-slate-300 hover:border-slate-600 hover:bg-slate-900'}`} data-id="element-546">
                      <div className="flex items-center justify-between" data-id="element-547">
                        <span className="font-medium" data-id="element-548">{option.label}</span>
                        {isSelected && <Check className="h-5 w-5 text-cyan-400" data-id="element-549" />}
                      </div>
                    </button>;
            })}
              </div>
            </div>)}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-slate-800" data-id="element-550">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 0} data-id="element-551">
            <ArrowLeft className="mr-2 h-4 w-4" data-id="element-552" />
            Back
          </Button>
          <Button variant="primary" onClick={handleNext} disabled={!canProceed()} data-id="element-553">
            {currentStep === totalSteps - 1 ? 'View Results' : 'Next Step'}
            <ArrowRight className="ml-2 h-4 w-4" data-id="element-554" />
          </Button>
        </div>
      </Card>
    </div>;
}
function RoadmapColumn({
  title,
  items,
  color
}: {
  title: string;
  items: string[];
  color: string;
}) {
  return <div data-id="element-555">
      <h4 className={`text-lg font-bold ${color} mb-4`} data-id="element-556">{title}</h4>
      <ul className="space-y-3" data-id="element-557">
        {items.map((item, index) => <li key={index} className="flex items-start text-sm text-slate-300" data-id="element-558">
            <span className="text-cyan-400 mr-2 mt-0.5" data-id="element-559">▸</span>
            <span data-id="element-560">{item}</span>
          </li>)}
      </ul>
    </div>;
}